import { ComponentType, useEffect, useMemo, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';
import { Field, FieldRenderProps } from 'react-final-form';

import {
  Input,
  Select,
  Typography,
  Box,
  Stack,
  colorOrToken,
  tokens,
} from '@cast/design-system';

import { toHumanReadableCron } from 'utils/cron';

import { DayInput } from './_components/DayInput';
import { HourInput } from './_components/HourInput';
import { InvalidInput } from './_components/InvalidInput';
import { MinuteInput } from './_components/MinuteInput';
import { MonthInput } from './_components/MonthInput';
import { WeekInput } from './_components/WeekInput';
import { constraintsByOption } from './_constants';
import { FormState, Option, options } from './_types';
import { StandardInputsContext } from './context';
import { cronToFormState, formStateToCron } from '../_utils';

import { useRffFormState } from '@cast/shared-rff-ui';

const inputsByType: Partial<Record<Option, ComponentType<any>>> = {
  Week: WeekInput,
  Day: DayInput,
  Hour: HourInput,
  Minute: MinuteInput,
  Month: MonthInput,
  '--': InvalidInput,
};

type StandardInputsProps = {
  value: string | null;
  onChange: (value?: string) => void;
  scheduleLabel?: string;
  timeZoneInput?: React.ReactNode;
};

const StandardInputs = ({
  value,
  onChange,
  scheduleLabel = 'Repeat every',
  timeZoneInput,
}: StandardInputsProps) => {
  const { mode } = useRffFormState();
  const isView = mode === 'view';
  const [formState, setFormState] = useState<FormState>(() => {
    if (value) {
      return cronToFormState(value) || constraintsByOption['--'].formState;
    }
    return constraintsByOption.Week.formState;
  });

  const isInvalid = formState.type === '--';
  const availableOptions: Option[] = isInvalid
    ? (options as unknown as Option[])
    : options.slice(1);

  const inputProps = constraintsByOption[formState.type].inputProps;
  const isFirstChange = useRef(true);
  useEffect(() => {
    if (isInvalid) {
      return;
    }
    const parsedCron = formStateToCron(formState);
    if (isFirstChange.current) {
      isFirstChange.current = false;
      if (mode !== 'create') {
        onChange(parsedCron);
      }
      return;
    } else {
      onChange(parsedCron);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState, isInvalid]);

  const humanReadableCron = useMemo(() => {
    if (!value) {
      return;
    }
    try {
      return toHumanReadableCron(value, { throwError: true });
    } catch (e) {
      console.error(e);
      Sentry.captureMessage('Invalid cron generated with standard inputs');
    }
    return undefined;
  }, [value]);

  const Inputs = inputsByType[formState.type];

  return (
    <StandardInputsContext.Provider
      value={{ state: formState, setState: setFormState, isView }}
    >
      <Stack>
        {isInvalid && (
          <Typography
            variant="P12R"
            color={colorOrToken('red.500', tokens.theme.fg.status.error)}
            mb={16}
          >
            The value entered in the crontab can&apos;t be displayed but will be
            used to execute the schedule.
          </Typography>
        )}
        {timeZoneInput}

        <Input
          type="number"
          label={scheduleLabel}
          value={formState.repeatEvery}
          onChange={(_, value) => {
            setFormState(
              (formState) =>
                ({
                  ...formState,
                  repeatEvery: Number(value),
                } as any)
            );
          }}
          readOnly={isView || formState?.type === 'Week'}
          endOutsideAdornment={
            <Select
              disabled={isView}
              options={availableOptions}
              value={formState?.type}
              onChange={(option: Option) => {
                setFormState(constraintsByOption[option].formState);
              }}
              testId="repeat-every-unit"
            />
          }
          sx={{
            '&.DS-Input-withOutsideEndAdornment': {
              gridTemplateColumns: '64px 1fr',
              columnGap: 8,
              '& .MuiInputLabel-root': { gridColumn: '1 / -1' },
              '& .DS-Dropdown-root': { width: '100%' },
            },
          }}
          {...inputProps}
          testId="repeat-every-number"
        />
        <Box my={16}>{Inputs && <Inputs />}</Box>
        {!!humanReadableCron && (
          <Typography
            variant="C12"
            color={colorOrToken(
              'grey.400',
              tokens.theme.fg.structural.secondary
            )}
            component="p"
            borderTop={tokens.theme.border.structural.neutral}
            pt={16}
            data-testid="standard-input-hint"
          >
            {humanReadableCron}
          </Typography>
        )}
      </Stack>
    </StandardInputsContext.Provider>
  );
};

type StandardInputsTabProps = {
  name: string;
  scheduleLabel?: string;
  timeZoneInput?: React.ReactNode;
};

export const StandardInputsTab = ({
  name,
  scheduleLabel,
  timeZoneInput,
}: StandardInputsTabProps) => {
  return (
    <Field
      name={name}
      render={(renderProps: FieldRenderProps<string>) => {
        const {
          input: { onChange, value },
        } = renderProps;
        return (
          <StandardInputs
            value={value}
            onChange={onChange}
            scheduleLabel={scheduleLabel}
            timeZoneInput={timeZoneInput}
          />
        );
      }}
    />
  );
};
